import { useIntl } from "react-intl";
import { useAbility } from "@casl/react";
import { urlPaths } from "enums/urlPaths";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";

// Permissions
import { AbilityContext } from "context/Ability";

// MUI components
import InfoIcon from "@mui/icons-material/Info";
import { Box, Breadcrumbs, Button, Divider, Link, Typography, useTheme } from "@mui/material";

// Custom components
import NavButton from "components/HeaderNavigation/NavButton";
import { Header, HeaderInner } from "components/Layout/LayoutContent";
import { BreadcrumbStep } from "components/PaceLabsDashboard/Breadcrumbs";
import HeaderActions from "components/HeaderActions";
import { LeadershipOverview } from "components/LeadershipOverview";
import { Management } from "components/Management";
import { TruncatedText } from "components/TruncatedText";

import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import CustomIcon from "components/CustomIcon";
import { buildParamsString } from "utils";
import { useAppDispatch, useAppSelector } from "app/hooks";

import { fetchICReport, getIcDashboard } from "store/slice/Project/icDashboard/icDashboard.slice";
import icons from "enums/icons";
import { getProjectCompanyData } from "store/slice/Project/projectData/ProjectSlice";
import { getInsightById } from "store/slice/Project/insights/insightsSlice";
import { shallowEqual } from "react-redux";
import { PathToImprovement } from "components/Projection/ProjectionBody/PathToImprovement";
import { ChartRouteNames } from "enums/chartEnums";
import { clearChart } from "store/slice/charts/chartsSlice";
import { showLoader } from "store/slice/UI";
import { isEmpty } from "lodash";
import Modal from "components/Modal";
import { THeaderConfigProps, headerConfig } from "components/Management/table-config";

const steps: BreadcrumbStep[] = [
    {
        title: "Leadership Overview",
        route: "overview",
        children: null,
    },
    {
        title: "Management & Board Assessment",
        route: "management",
        children: null,
    },
    {
        title: "Path To Improvement",
        route: "path-to-improvement",
        children: null,
    },
];

const LeadershipDashboard = () => {
    const intl = useIntl();
    const theme = useTheme();
    const ability = useAbility(AbilityContext);

    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const { pathname } = useLocation();
    const [searchParams] = useSearchParams();

    const canRequestICDashboard = ability.can("see", "LD_PROJECT_VIEW");

    // Management Table Header
    const [tableHeader, setTableHeader] = useState<THeaderConfigProps[]>(headerConfig);

    // Url params
    const { companyId } = useParams();
    const projectId = searchParams.get("projectId");
    const insightId = searchParams.get("insightId");
    const briefId = searchParams.get("briefId");
    const searchAiId = searchParams.get("searchAiId");
    const params = buildParamsString({ projectId, insightId, briefId, searchAiId });

    const { data, status, loadedParams } = useAppSelector(getIcDashboard);
    const { data: company } = useAppSelector(getProjectCompanyData);
    const { data: insightData } = useAppSelector(getInsightById(Number(insightId)), shallowEqual);

    const showIncompleteDataModal = useMemo(
        () => !companyId || !projectId || !insightId || status === "error" || (status === "done" && isEmpty(data)),
        [companyId, projectId, insightId, status, data]
    );

    const handleGoBackToDashboard = () => {
        navigate(`${urlPaths.ProjectDashboard}/${projectId}`);
    };

    const handleViewInsight = () => {
        dispatch(clearChart());
        navigate(`${urlPaths.Projection}/${companyId}?projectId=${projectId}&insightId=${insightId}`);
    };

    useEffect(() => {
        if (!canRequestICDashboard) {
            handleGoBackToDashboard();
        }

        const requestData = async () => {
            const isAlreadyLoaded =
                !isEmpty(data) && loadedParams?.projectId === projectId && loadedParams?.insightId === insightId;

            if (status === "fetching" || isAlreadyLoaded) {
                return;
            }

            dispatch(showLoader(true));
            await dispatch(fetchICReport({ projectId, insightId }));
            dispatch(showLoader(false));
        };

        requestData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Box>
            <Header backgroundColor="primary">
                <HeaderInner>
                    <Typography variant="h5" sx={{ fontWeight: 600, display: "flex" }}>
                        <Box sx={{ mr: 1 }}>{intl.formatMessage({ id: "leadershipDashboard.main.title" })}</Box>
                        <Box sx={{ display: "flex", maxWidth: 200 }}>
                            <TruncatedText
                                width={200}
                                tooltipPlacement="bottom-start"
                                sx={{
                                    fontSize: "24px",
                                    fontWeight: 600,
                                }}
                            >
                                {insightData?.data.name}
                            </TruncatedText>
                        </Box>
                    </Typography>
                    <HeaderActions isLeadershipAction selectedColumns={tableHeader} />
                    <NavButton
                        variant="outlined"
                        translationId="paceLabsDashbard.insights.back.to.dashboard"
                        isBackButton
                        icon={null}
                        onClick={handleGoBackToDashboard}
                    />
                </HeaderInner>
            </Header>

            {/* TABS Section */}
            <Box marginTop={2} display="flex" alignItems="center" justifyContent="space-between" py={1} px={2}>
                <Breadcrumbs
                    separator={
                        <NavigateNextIcon
                            fontSize="small"
                            sx={{ marginRight: 2, marginLeft: 2, color: theme.palette.primary.main }}
                        />
                    }
                >
                    {steps.map((step, index) => {
                        const activeStep = pathname.includes(step.route);

                        return (
                            <Link
                                key={index}
                                sx={{ textDecoration: "none" }}
                                onClick={(e) => {
                                    e.preventDefault();

                                    if (step.route === "path-to-improvement") {
                                        navigate(
                                            `${urlPaths.PathToImprovement}/${companyId}/${ChartRouteNames.Bridge}?${params}`
                                        );
                                    } else {
                                        navigate(
                                            `${urlPaths.LeadershipDashboard}/${step.route}/${companyId}?${params}`
                                        );
                                    }
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: 16,
                                        color: activeStep ? theme.palette.primary.main : theme.palette.text.secondary,
                                        fontWeight: activeStep ? 700 : 400,
                                    }}
                                >
                                    {step.title}
                                </Typography>
                            </Link>
                        );
                    })}
                </Breadcrumbs>

                <Button
                    variant="contained"
                    size="small"
                    sx={{ fontSize: 13 }}
                    startIcon={<CustomIcon icon={icons.sparkles} />}
                    onClick={handleViewInsight}
                >
                    Explore Leadership Insights
                </Button>
            </Box>

            <Divider orientation="horizontal" variant="middle" flexItem sx={{ marginTop: 1, marginBottom: 1 }} />

            {/* MAIN CONTENT */}
            {status !== "fetching" && (
                <Box>
                    {pathname.includes(urlPaths.LeadershipOverview) && (
                        <LeadershipOverview data={data} companyName={company?.name ?? ""} />
                    )}
                    {pathname.includes(urlPaths.LeadershipManagement) && (
                        <Management
                            tableHeader={tableHeader}
                            setTableHeader={setTableHeader}
                            data={data?.leadershipTeam ?? null}
                        />
                    )}
                    {pathname.includes(urlPaths.PathToImprovement) && <PathToImprovement />}
                </Box>
            )}

            {/* Complete Insight Modal */}
            {showIncompleteDataModal && (
                <Modal open={showIncompleteDataModal}>
                    <Box
                        sx={{
                            width: "100%",
                            maxWidth: 500,
                            p: 2,
                            borderRadius: 0.8,
                            backgroundColor: "background.blue",
                        }}
                    >
                        <Box display="flex" alignItems="center">
                            <InfoIcon />
                            <Typography sx={{ fontSize: 18, ml: 1 }}>
                                {intl.formatMessage({ id: "leadershipDashboard.errorModal.title" })}
                            </Typography>
                        </Box>
                        <Box pt={1.2} pb={1}>
                            <Typography>
                                {intl.formatMessage({ id: "leadershipDashboard.errorModal.message" })}
                            </Typography>
                        </Box>
                        <Box display="flex" justifyContent="flex-end">
                            <NavButton isBackButton onClick={handleGoBackToDashboard}>
                                {intl.formatMessage({ id: "button.goToDashBoard" })}
                            </NavButton>
                        </Box>
                    </Box>
                </Modal>
            )}
        </Box>
    );
};

export default LeadershipDashboard;
